import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, BuildingIcon, CalendarIcon, CreditCardIcon, FileTextIcon } from 'lucide-react';
import { Settlement } from '../types/Transaction';
export function SettlementDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data
  const settlement: Settlement = {
    id: id || '1',
    settlementId: 'SETTLE-2024-001',
    amount: 145000,
    currency: 'MYR',
    status: 'completed',
    date: '2024-01-15T09:00:00',
    payoutDate: '2024-01-17T14:30:00',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    bankName: 'Maybank',
    bankAccountNumber: '****1234',
    bankAccountName: 'ABC Store Pvt Ltd',
    branchCode: '001',
    transactionCount: 45,
    transactionIds: ['TXN-2024-001234', 'TXN-2024-001235', 'TXN-2024-001236', 'TXN-2024-001237', 'TXN-2024-001238'],
    fee: 4350,
    netAmount: 140650,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T09:00:00',
      status: 'initiated',
      description: 'Settlement batch created with 45 transactions',
      metadata: {
        transaction_count: '45',
        total_amount: 'RM 145,000'
      }
    }, {
      id: '2',
      timestamp: '2024-01-16T10:00:00',
      status: 'processing',
      description: 'Processing payout to merchant bank account',
      metadata: {
        bank: 'Maybank',
        account: '****1234'
      }
    }, {
      id: '3',
      timestamp: '2024-01-17T14:30:00',
      status: 'completed',
      description: 'Funds successfully transferred to merchant account',
      metadata: {
        reference_number: 'REF123456789',
        net_amount: 'RM 140,650'
      }
    }]
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      reversed: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-8470">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  return <div className="space-y-6" data-id="element-8471">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-8472">
        <button onClick={() => navigate('/settlements')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-8473">
          <ArrowLeftIcon size={20} data-id="element-8474" />
          Back to Settlements
        </button>
        <div className="flex items-center gap-3" data-id="element-8475">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-8476">
            <DownloadIcon size={18} data-id="element-8477" />
            Download Statement
          </button>
        </div>
      </div>
      {/* Settlement Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8478">
        <div className="flex items-start justify-between mb-6" data-id="element-8479">
          <div data-id="element-8480">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-8481">
              {settlement.settlementId}
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-8482">
              Settlement for {settlement.merchantName}
            </p>
          </div>
          {getStatusBadge(settlement.status)}
        </div>
        {/* Amount Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6 pb-6 border-b border-gray-200" data-id="element-8483">
          <div data-id="element-8484">
            <div className="text-sm text-gray-500" data-id="element-8485">Gross Amount</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-8486">
              RM {settlement.amount.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-8487">
            <div className="text-sm text-gray-500" data-id="element-8488">Processing Fee</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-8489">
              RM {settlement.fee.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-8490">
            <div className="text-sm text-gray-500" data-id="element-8491">Net Amount</div>
            <div className="mt-1 text-2xl font-semibold text-green-600" data-id="element-8492">
              RM {settlement.netAmount.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-8493">
            <div className="text-sm text-gray-500" data-id="element-8494">Transactions</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-8495">
              {settlement.transactionCount}
            </div>
          </div>
        </div>
        {/* Settlement Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8496">
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-8497">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8498">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-8499" />
              Merchant Information
            </h3>
            <div className="space-y-3" data-id="element-8500">
              <div data-id="element-8501">
                <div className="text-sm text-gray-500" data-id="element-8502">Merchant Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8503">
                  {settlement.merchantName}
                </div>
              </div>
              <div data-id="element-8504">
                <div className="text-sm text-gray-500" data-id="element-8505">Merchant ID</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8506">
                  {settlement.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Bank Information */}
          <div className="space-y-4" data-id="element-8507">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8508">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-8509" />
              Bank Account Information
            </h3>
            <div className="space-y-3" data-id="element-8510">
              <div data-id="element-8511">
                <div className="text-sm text-gray-500" data-id="element-8512">Bank Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8513">
                  {settlement.bankName}
                </div>
              </div>
              <div data-id="element-8514">
                <div className="text-sm text-gray-500" data-id="element-8515">Account Number</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8516">
                  {settlement.bankAccountNumber}
                </div>
              </div>
              <div data-id="element-8517">
                <div className="text-sm text-gray-500" data-id="element-8518">Account Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8519">
                  {settlement.bankAccountName}
                </div>
              </div>
              <div data-id="element-8520">
                <div className="text-sm text-gray-500" data-id="element-8521">Branch Code</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8522">
                  {settlement.branchCode}
                </div>
              </div>
            </div>
          </div>
          {/* Date Information */}
          <div className="space-y-4" data-id="element-8523">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-8524">
              <CalendarIcon size={20} className="text-blue-600" data-id="element-8525" />
              Timeline
            </h3>
            <div className="space-y-3" data-id="element-8526">
              <div data-id="element-8527">
                <div className="text-sm text-gray-500" data-id="element-8528">Settlement Date</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-8529">
                  {new Date(settlement.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              {settlement.payoutDate && <div data-id="element-8530">
                  <div className="text-sm text-gray-500" data-id="element-8531">Payout Date</div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-8532">
                    {new Date(settlement.payoutDate).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Included Transactions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8533">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2" data-id="element-8534">
          <FileTextIcon size={20} className="text-blue-600" data-id="element-8535" />
          Included Transactions ({settlement.transactionCount})
        </h2>
        <div className="space-y-2" data-id="element-8536">
          {settlement.transactionIds.map(txnId => <div key={txnId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100" data-id="element-8537">
              <span className="text-sm font-medium text-gray-900" data-id="element-8538">{txnId}</span>
              <button onClick={() => navigate(`/sale-detail/${txnId}`)} className="text-sm text-blue-600 hover:text-blue-900" data-id="element-8539">
                View Details
              </button>
            </div>)}
        </div>
      </div>
      {/* Settlement Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8540">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-8541">
          Settlement Lifecycle
        </h2>
        <div className="space-y-6" data-id="element-8542">
          {settlement.lifecycle.map((event, index) => <div key={event.id} className="relative" data-id="element-8543">
              {index !== settlement.lifecycle.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-8544"></div>}
              <div className="flex items-start gap-4" data-id="element-8545">
                <div className="flex-shrink-0" data-id="element-8546">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-8547">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-8548"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-8549">
                  <div className="flex items-center justify-between" data-id="element-8550">
                    <div className="flex items-center gap-3" data-id="element-8551">
                      <p className="text-sm font-medium text-gray-900" data-id="element-8552">
                        {event.description}
                      </p>
                      {getStatusBadge(event.status)}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-8553">
                      {new Date(event.timestamp).toLocaleString('en-GB', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                    </p>
                  </div>
                  {event.metadata && <div className="mt-2 p-3 bg-gray-50 rounded-lg" data-id="element-8554">
                      <div className="grid grid-cols-2 gap-2" data-id="element-8555">
                        {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-8556">
                            <span className="text-xs text-gray-500" data-id="element-8557">
                              {key.replace('_', ' ').toUpperCase()}:
                            </span>
                            <span className="text-xs font-medium text-gray-900 ml-1" data-id="element-8558">
                              {String(value)}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}